<template>
  <div id="app" class="product-page">
    <!-- Header with dynamic class based on reviewed status -->
    <header class="main-header" :class="bannerClass">
      <h1>AlliumAI Data Enhancement</h1>
    </header>

    <!-- Add a transient alert banner for "No more unreviewed" -->
    <transition name="fade">
      <div v-if="showNoMoreUnreviewed" class="alert-banner">
        No more unreviewed data left!
      </div>
    </transition>

    <!-- Login View -->
    <Login v-if="!isAuthenticated" @login-success="onLoginSuccess" />

    <!-- Main Content -->
    <main v-else>
      <div class="main-content">
        <!-- Catalog View -->

        <!-- Taxonomy Editor View -->
        <div>
          <TaxonomyEditor
            :attributesData="attributeFilters"
            @navigate="navigateTo"
            @updateAttributes="updateAttributes"
          />
        </div>
      </div>
    </main>

    <footer class="footer"></footer>

    <!-- Snackbar for save feedback -->
    <div v-if="showSnackbar" :class="['snackbar', snackbarColor]">
      {{ snackbarMessage }}
    </div>
  </div>
</template>

<script>
import TaxonomyEditor from './components/TaxonomyEditor/TaxonomyEditor.vue';
import Login from './components/Login.vue';
import '@mdi/font/css/materialdesignicons.css'

const FUNCTION_URL = "https://data-reviewer-844472556697.us-central1.run.app"; // replace with your actual function URL

export default {
  name: 'App',
  components: {    
    TaxonomyEditor,        
    Login,
  },
  data() {
    return {
      showNoMoreUnreviewed: false, // triggers the banner
      showAddNotePopup: false,
      reviewNote: '',
      snackbarMessage: '',
      showSnackbar: false,
      snackbarColor: 'success', // could be 'success' or 'error'
      confidenceOrder: ['certain', 'pretty_sure', 'not_sure'],
      manor_taxonomy_en: {
        ClothingType: ["Evening dresses", "Cocktail dresses", "Knit dresses", "Denim dresses", "Summer dresses", "Casual dresses", "Lace dresses", "Dirndl"],
        Material: ["Cotton", "Linen", "Wool", "Viscose", "Silk", "Leather", "Polyester", "Lyocell", "Cashmere"],
        Length: ["Knee-length", "Maxi", "Midi", "Mini", "Calf-length", "3/4-length"],
        Neckline: ["Round neck", "V-neck", "Boat neckline", "Halter neck", "Sweetheart", "Back neckline", "Cache-coeur", "Square neckline", "Button up", "Zip up"],
        CollarType: ["Turtleneck", "Stand-up collar", "Henley", "Shawl collar", "Waterfall", "Hood", "Revere collar", "Bow collar", "Turn-down collar", "Troyer", "Hood", "Peter Pan collar"],
        SleeveLength: ["Short sleeve", "Long sleeve", "3/4 sleeve", "Sleeveless", "Half sleeve"],
        Pattern: ["Solid", "Checkered", "Polka dot", "Animal", "Camouflage", "Paisley", "Ethnic", "Striped", "Houndstooth", "Pinstripe", "Color-block", "Gradient", "Herringbone", "Floral", "Ornaments", "All-over print"],
        Fit: ["Body fit", "Classic fit", "Comfort fit", "Loose fit", "Regular fit", "Relaxed fit", "Skinny fit", "Slim fit", "Straight fit", "Tailored fit"],
        Occasion: ["Prom", "Casual", "Wedding", "Business", "Festive", "Party", "Beach", "Christmas"],
        SpecialSizes: ["Plus size", "Petite", "Tall", "Maternity"]
      },
      reviewMode: false,
      skipReviewed: true,
      onlyShowEditedData: false,
      showReviewedData: true,
      reviewedMap: {}, 
      editedManorAttributes: {},
      selectedFlaggedAttribute: '', 
      flaggedAttributesList: [],
      selectedAttributeSource: '',
      selectedManorAttributeSource: '',
      selectedAlliumAttributeSource: '',      
      isAuthenticated: false,
      products: {},
      allProductIds: [],
      filteredProductIds: [],
      currentProductIndex: null,
      selectedLanguage: 'english',
      selectedVariantId: '',
      selectedColor: '',
      selectedSize: '',
      brands: [],
      selectedBrand: '',
      variantSearchInput: '',
      beforeUpdatedSelected: false,
      selectedManorReweightedMethod: 'priority_sorting',
      selectedAlliumReweightedMethod: 'priority_sorting',
      selectedFlaggingStrategy: 'disagreement_existing_image',
      isEditedData: false,
      hasEdits: false,
      editedAttributes: {},
      editedDetails: {},
      attributeFilters: {
        dressCategory: {
          attributes: [],
          values: {},
        },
        manorTaxonomy: {
          attributes: [],
          values: {},
        },
        alliumTaxonomy: {
          attributes: [],
          values: {},
        },
      },
      selectedAttributeFilter: {
        type: '',
        attribute: '',
        value: '',
      },
    };
  },
  computed: {
    isCurrentlyReviewed() {
      // A product is “reviewed” if reviewedMap[productId]?.reviewed === true
      return this.currentProductId && this.reviewedMap[this.currentProductId]?.reviewed;
    },
    reweightedMethods() {
      return ['priority_sorting', 'voting_mode', 'voting_weighted', 'priority_image'];
    },
    flaggingStrategies() {
      return ['no_multi_source', 'no_structured_source', 'no_source', 'disagreement', 'disagreement_existing_image'];
    },    
    currentVariantId() {
      if (this.selectedVariantId) {
        return this.selectedVariantId;
      } else if (this.variantIds.length > 0) {
        return this.variantIds[0];
      }
      return '';
    },
    currentVariantData() {
      const variantData = this.currentProductVariants[this.currentVariantId] || {};
      return {
        ...variantData,
        'Manor URL Link': variantData['Manor URL Link'] || '',
      };
    },
    languages() {
      return ['english', 'german', 'french', 'italian'];
    },
    availableColors() {
      const colors = new Set();
      for (const variantId of this.variantIds) {
        const variant = this.currentProductVariants[variantId];
        const beforeData = variant.before[this.selectedLanguage];

        const primaryColor =
          beforeData?.variant_data?.['Primary Color'] ||
          beforeData?.['Primary Color'] ||
          beforeData?.['primaryColor'];

        if (primaryColor) {
          colors.add(primaryColor);
        }
      }
      return Array.from(colors);
    },
    afterAvailableColors() {
      const colors = new Set();
      for (const variantId of this.variantIds) {
        const variant = this.currentProductVariants[variantId];
        const afterData = variant.after?.[this.selectedLanguage];
        if (!afterData) continue;

        const primaryColorHex =
          afterData.variant_data?.['Primary Color Hex Code'] ||
          afterData.variant_data?.['primaryColorHexCode'] ||
          null;

        if (primaryColorHex) {
          colors.add(primaryColorHex);
        }
      }
      return Array.from(colors);
    },
    availableSizes() {
      const sizes = new Set();
      for (const variantId of this.variantIds) {
        const variant = this.currentProductVariants[variantId];
        const beforeData = variant.before[this.selectedLanguage];

        const size = beforeData?.variant_data?.['Size'] || beforeData?.attributes?.['Size'];
        if (size) sizes.add(size);
      }
      return Array.from(sizes);
    },
    isPreviousDisabled() {
      return this.currentProductIndex === 0;
    },
    isNextDisabled() {
      return this.currentProductIndex >= this.filteredProductIds.length - 1;
    },
    alliumFlaggedAttributes() {
      return this.currentAfterData.alliumFlaggedAttributes || {};
    },
    manorFlaggedAttributes() {
      return this.currentAfterData.manorFlaggedAttributes || {};
    },
    bannerClass() {
      if (this.reviewMode && this.isCurrentlyReviewed) {
        return 'reviewed-banner';
      } else {
        return 'not-reviewed-banner';
      }
    }
  },

  methods: {
    async loadReviewStatus(variantId) {
      try {
        const folder = "manor-apparel-pilot/manual-labels2"; // your folder
        const res = await fetch(
          `${this.FUNCTION_URL}?action=load&key=${variantId}&folder=${folder}`
        );

        if (res.status === 404) {
          // Not reviewed
          console.log(`Variant ${variantId} not reviewed yet.`);
          this.reviewedMap[variantId] = { reviewed: false };
          return;
        }

        if (!res.ok) {
          console.error("Error loading review status:", res.statusText);
          return;
        }

        const json = await res.json();
        if (json.reviewed) {
          // Mark as reviewed in local memory
          this.reviewedMap[variantId] = { reviewed: true, data: json.data };
          console.log(`Variant ${variantId} was already reviewed (data loaded).`);
        } else {
          // It's unreviewed
          this.reviewedMap[variantId] = { reviewed: false };
          console.log(`Variant ${variantId} is unreviewed.`);
        }
      } catch (err) {
        console.error("Error loading review status:", err);
      }
    },

    loadCurrentPairData() {
      // Make sure we have a valid currentPairIndex
      if (
        this.currentPairIndex == null ||
        this.filteredPairs.length === 0 ||
        this.currentPairIndex >= this.filteredPairs.length
      ) {
        // No item to load
        return;
      }

      const { productId, variantId } = this.filteredPairs[this.currentPairIndex];

      // Update your component to reflect the newly-chosen product/variant
      this.currentProductId = productId;
      this.currentVariantId = variantId;

      // Example: call loadReviewStatus(variantId) to see if it’s already reviewed
      this.loadReviewStatus(productId);

      // Optionally, load images for this product/variant, etc.
      // e.g.: this.loadImagesForVariant(variantId);
    },

    isProductEdited(productId) {
      return !!this.reviewedMap[productId]?.reviewed;
    },
    openAddNotePopup() {
      this.showAddNotePopup = true;
      this.reviewNote = this.currentAfterData.Notes || '';
    },
    closeAddNotePopup() {
      this.showAddNotePopup = false;
      this.reviewNote = '';
    },
    saveNote() {
      // Put the note in "Notes" field. 
      // If you're storing "Notes" in currentAfterData:
      if (!this.currentAfterData) this.currentAfterData = {};
      this.currentAfterData.Notes = this.reviewNote;
      
      // Close popup
      this.closeAddNotePopup();
    },
    toggleSkipReviewed() {
      this.skipReviewed = !this.skipReviewed;
      if (this.skipReviewed) {
        // Make sure "Only Show Edited" is off
        this.onlyShowEditedData = false;
      }
      // Filter local data
      this.applyReviewFilters();
    },

    toggleOnlyShowEditedData() {
      this.onlyShowEditedData = !this.onlyShowEditedData;
      if (this.onlyShowEditedData) {
        // Make sure "Skip Reviewed" is off
        this.skipReviewed = false;
      }
      // Filter local data
      this.applyReviewFilters();
    },

    async fetchNextUnreviewedItem() {
      try {
        const url = `${FUNCTION_URL}?action=getNextUnreviewedItem&folder=manor-apparel-pilot/manual-labels2`;
        const response = await fetch(url);
        const data = await response.json();

        if (data.done) {
          // No more unreviewed data => show our alert
          this.showNoMoreUnreviewed = true;
          // Fade out after 3 seconds
          setTimeout(() => {
            this.showNoMoreUnreviewed = false;
          }, 3000);
          return;
        }

        // Otherwise, store the returned item...
        this.$set(this.products, data.itemId, {
          before: data.data.before,
          after: data.data.after,
        });
        this.selectedVariantId = data.itemId;
        await this.loadImagesForVariant(this.selectedVariantId);

      } catch (error) {
        console.error('Error fetching unreviewed item:', error);
      }
    },

    async fetchNextReviewedItem() {
      try {
        // 1) Call your serverless function
        const url = `${FUNCTION_URL}?action=getNextReviewedItem&folder=manor-apparel-pilot/manual-labels2`;
        const response = await fetch(url);
        const data = await response.json();

        // 2) Check if any reviewed items exist
        if (data.done) {
          alert('No more reviewed items!');
          return;
        }

        // 3) Insert the newly fetched product variant into your local `products` object,
        //    so your computed properties and watchers can pick it up:
        this.$set(this.products, data.itemId, {
          before: data.data.before,
          after: data.data.after,
          // ... add any other fields you might need (brand, image_urls, etc.)
        });

        // 4) Update your selectedVariantId (so your UI knows which variant to display)
        this.selectedVariantId = data.itemId;
        this.currentProductIndex = null; 
        this.currentVariantDataFromServer = data.data;

        // 5) Optionally load images for this variant so they stay in sync
        await this.loadImagesForVariant(this.selectedVariantId);

        console.log('Fetched reviewed item:', data.itemId);
      } catch (error) {
        console.error('Error fetching next reviewed item:', error);
      }
    },

    async loadImagesForVariant(variantId) {
      try {
        // If your server response has data.images, we can set them directly.
        if (this.currentVariantDataFromServer && this.currentVariantDataFromServer.images) {
          // e.g. store them to your "currentBeforeData" or "currentAfterData"
          this.currentBeforeData.images = this.currentVariantDataFromServer.images;
        }
        // Or fetch from the server if needed. 
        // The main point is: we only load images *after* we know which variant
        // so they stay in sync.
      } catch (err) {
        console.error('Error loading images for variant:', variantId, err);
      }
    },
    getManorOptions(key) {
      return this.manor_taxonomy_en[key] || [];
    },
    collectFlaggedAttributes() {
      const flaggedAttributesSet = new Set();

      for (const productId in this.products) {
        const variants = this.products[productId];

        for (const variantId in variants) {
          const variant = variants[variantId];
          const afterData = variant.after?.[this.selectedLanguage];

          if (afterData && afterData.predictions_flagged) {
            const alliumFlaggedAttributes = afterData.predictions_flagged.attributes?.[this.selectedFlaggingStrategy];
            if (alliumFlaggedAttributes) {
              for (const attrKey in alliumFlaggedAttributes) {
                if (alliumFlaggedAttributes[attrKey]) {
                  flaggedAttributesSet.add(attrKey);
                }
              }
            }

            const manorFlaggedAttributes = afterData.predictions_flagged.manor_taxonomy_attributes?.[this.selectedFlaggingStrategy];
            if (manorFlaggedAttributes) {
              for (const attrKey in manorFlaggedAttributes) {
                if (manorFlaggedAttributes[attrKey]) {
                  flaggedAttributesSet.add(attrKey);
                }
              }
            }
          }
        }
      }

      this.flaggedAttributesList = Array.from(flaggedAttributesSet).sort();
    },
    getSourceKey(selectedSource, type) {
      const sourceMap = {
        Image: type === 'allium' ? 'image_allium' : 'image_manor',
        Existing: type === 'allium' ? 'existing_allium' : 'existing_manor',
        Vendor: type === 'allium' ? 'vendor_allium' : 'vendor_manor',
        External: type === 'allium' ? 'external_allium' : 'external_manor',
      };
      return sourceMap[selectedSource] || '';
    },

    navigateTo(view) {
      this.currentView = view;
    },

    async onLoginSuccess() {
      console.log('Login successful. Updating authentication state.');
      this.isAuthenticated = true;
      this.currentView = 'attributeManagement';      
    },

    buildAttributeData() {
      try {
        this.attributeFilters = {
          dressCategory: {
            attributes: [],
            values: {},
          },
          manorTaxonomy: {
            attributes: [],
            values: {},
          },
          alliumTaxonomy: {
            attributes: [],
            values: {},
          },
        };

        for (const productId in this.products) {
          const variants = this.products[productId];
          for (const variantId in variants) {
            const variant = variants[variantId];
            const afterData = variant.after?.[this.selectedLanguage];
            if (!afterData) continue;

            const dressCategory = afterData['Dress Category'] || afterData['dressCategory'];
            if (dressCategory) {
              if (!this.attributeFilters.dressCategory.attributes.includes('Dress Category')) {
                this.attributeFilters.dressCategory.attributes.push('Dress Category');
              }
              if (!this.attributeFilters.dressCategory.values['Dress Category']) {
                this.attributeFilters.dressCategory.values['Dress Category'] = new Set();
              }
              this.attributeFilters.dressCategory.values['Dress Category'].add(dressCategory);
            }

            const manorAttrs = afterData.manor_taxonomy_attributes || {};
            Object.entries(manorAttrs).forEach(([key, value]) => {
              if (typeof value === 'string') {
                if (!this.attributeFilters.manorTaxonomy.attributes.includes(key)) {
                  this.attributeFilters.manorTaxonomy.attributes.push(key);
                  this.attributeFilters.manorTaxonomy.values[key] = new Set();
                }
                this.attributeFilters.manorTaxonomy.values[key].add(value);
              }
            });

            const alliumAttrs = afterData.attributes || {};
            Object.entries(alliumAttrs).forEach(([key, value]) => {
              if (typeof value === 'string') {
                if (!this.attributeFilters.alliumTaxonomy.attributes.includes(key)) {
                  this.attributeFilters.alliumTaxonomy.attributes.push(key);
                  this.attributeFilters.alliumTaxonomy.values[key] = new Set();
                }
                this.attributeFilters.alliumTaxonomy.values[key].add(value);
              }
            });
          }
        }

        for (const category in this.attributeFilters) {
          this.attributeFilters[category].attributes.sort();
          for (const attr in this.attributeFilters[category].values) {
            this.attributeFilters[category].values[attr] = 
              Array.from(this.attributeFilters[category].values[attr]).sort();
          }
        }

        console.log('Built attribute filters:', this.attributeFilters);
      } catch (error) {
        console.error('Error building attribute data:', error);
      }
      this.collectFlaggedAttributes();
    },

    getAttributesForCatalog() {
      return {
        dressCategories: this.attributeFilters.dressCategory.values['Dress Category'] || [],
        manorTaxonomyAttributes: {
          attributes: this.attributeFilters.manorTaxonomy.attributes,
          values: this.attributeFilters.manorTaxonomy.values,
        },
        alliumTaxonomyAttributes: {
          attributes: this.attributeFilters.alliumTaxonomy.attributes,
          values: this.attributeFilters.alliumTaxonomy.values,
        },
      };
    },

    changeLanguage() {
      this.resetVariantSelection();
      this.buildAttributeData();
      this.collectFlaggedAttributes();
      this.applyBrandAndAttributeFilters();
    },

    async previousProduct() {
      if (this.currentProductIndex > 0) {
        this.currentProductIndex--;
        this.resetVariantSelection();
        this.selectedVariantId = this.variantIds[0] || '';
        await this.loadCurrentItemData();
      }
    },

    async nextProduct() {
      if (this.currentProductIndex < this.filteredProductIds.length - 1) {
        this.currentProductIndex++;
        this.resetVariantSelection();
        this.selectedVariantId = this.variantIds[0] || '';
        await this.loadCurrentItemData();
      }
    },

    selectColor(color) {
      this.selectedColor = color;
      this.updateVariantBySelection();
    },

    selectSize(size) {
      this.selectedSize = size;
      this.updateVariantBySelection();
    },

    updateVariantBySelection() {
      for (const variantId of this.variantIds) {
        const variant = this.currentProductVariants[variantId];
        const beforeData = variant.before[this.selectedLanguage];

        const variantColor =
          beforeData?.variant_data?.['Primary Color'] ||
          beforeData?.['Primary Color'] ||
          beforeData?.['primaryColor'];

        const variantSize = beforeData?.variant_data?.['Size'] || beforeData?.attributes?.['Size'];

        if (
          (!this.selectedColor || variantColor === this.selectedColor) &&
          (!this.selectedSize || variantSize === this.selectedSize)
        ) {
          this.selectedVariantId = variantId;
          return;
        }
      }
      this.selectedVariantId = this.variantIds[0] || '';
    },

    resetVariantSelection() {
      this.selectedVariantId = '';
      this.selectedColor = '';
      this.selectedSize = '';
    },

    onAttributeFilterTypeChange() {
      this.selectedAttributeFilter.attribute = '';
      this.selectedAttributeFilter.value = '';
      this.applyBrandAndAttributeFilters();
    },

    onAttributeNameChange() {
      this.selectedAttributeFilter.value = '';
      this.applyBrandAndAttributeFilters();
    },

    applyAttributeFilter() {
      this.applyBrandAndAttributeFilters();
    },

    applyBrandAndAttributeFilters() {
      let filteredIds = [...this.allProductIds];

      // Apply brand filter
      if (this.selectedBrand) {
        filteredIds = filteredIds.filter((productId) => {
          const variants = this.products[productId];
          return Object.values(variants).some(
            variant => variant['Brand'] === this.selectedBrand
          );
        });
      }

      // Apply attribute filter
      if (
        this.selectedAttributeFilter.type &&
        this.selectedAttributeFilter.attribute &&
        this.selectedAttributeFilter.value
      ) {
        filteredIds = filteredIds.filter((productId) => {
          const variants = this.products[productId];
          return Object.values(variants).some(variant => {
            const afterData = variant.after?.[this.selectedLanguage];
            if (!afterData) return false;

            if (this.selectedAttributeFilter.type === 'dressCategory') {
              const dressCategory = afterData['Dress Category'] || afterData['dressCategory'];
              return dressCategory === this.selectedAttributeFilter.value;
            } else if (this.selectedAttributeFilter.type === 'manorTaxonomy') {
              const manorAttrs = afterData.manor_taxonomy_attributes || {};
              return manorAttrs[this.selectedAttributeFilter.attribute] === this.selectedAttributeFilter.value;
            } else if (this.selectedAttributeFilter.type === 'alliumTaxonomy') {
              const alliumAttrs = afterData.attributes || {};
              return alliumAttrs[this.selectedAttributeFilter.attribute] === this.selectedAttributeFilter.value;
            }
            return false;
          });
        });
      }

      // Apply flagged attribute filter
      if (this.selectedFlaggedAttribute) {
        filteredIds = filteredIds.filter((productId) => {
          const variants = this.products[productId];
          return Object.values(variants).some((variant) => {
            const afterData = variant.after?.[this.selectedLanguage];
            if (!afterData) return false;

            const predictionsFlagged = afterData.predictions_flagged;
            const alliumFlaggedAttributes = predictionsFlagged?.attributes?.[this.selectedFlaggingStrategy];
            const manorFlaggedAttributes = predictionsFlagged?.manor_taxonomy_attributes?.[this.selectedFlaggingStrategy];

            return (
              (alliumFlaggedAttributes && alliumFlaggedAttributes[this.selectedFlaggedAttribute]) ||
              (manorFlaggedAttributes && manorFlaggedAttributes[this.selectedFlaggedAttribute])
            );
          });
        });
      }

      this.filteredProductIds = filteredIds;
      this.currentProductIndex = this.filteredProductIds.length > 0 ? 0 : null;
      this.resetVariantSelection();
    },

    onBrandChange() {
      this.applyBrandAndAttributeFilters();
    },

    onVariantSearch() {
      const variantNumber = this.variantSearchInput.trim();
      if (!variantNumber) return;

      for (let i = 0; i < this.allProductIds.length; i++) {
        const productId = this.allProductIds[i];
        const variants = this.products[productId];
        if (variants && variants[variantNumber]) {
          const filteredIndex = this.filteredProductIds.indexOf(productId);
          if (filteredIndex === -1) {
            this.filteredProductIds.splice(this.currentProductIndex + 1, 0, productId);
            this.currentProductIndex = this.filteredProductIds.indexOf(productId);
          } else {
            this.currentProductIndex = filteredIndex;
          }
          this.selectedVariantId = variantNumber;
          return;
        }
      }
      alert('Variant not found.');
    },

    changeManorReweightedMethod() {
    },

    changeAlliumReweightedMethod() {
    },

    changeFlaggingStrategy() {
      this.selectedFlaggedAttribute = '';
      this.collectFlaggedAttributes();
      this.applyBrandAndAttributeFilters();
    },

    formatOptionLabel(option) {
      return option
        .replace(/_/g, ' ')
        .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1));
    },

    toggleReviewMode() {
      this.reviewMode = !this.reviewMode;

      // If we are *exiting* review mode, reset skip/onlyShow
      if (!this.reviewMode) {
        this.skipReviewed = false;
        this.onlyShowEditedData = false;
        // Possibly showReviewedData = true, etc.
        this.applyReviewFilters(); // or applyBrandAndAttributeFilters()
      }
    },

    cycleConfidence(key) {
      const current = this.editedManorAttributes[key].confidence;
      let nextIndex = (this.confidenceOrder.indexOf(current) + 1) % this.confidenceOrder.length;
      this.editedManorAttributes[key].confidence = this.confidenceOrder[nextIndex];
    },
    confidenceClass(confidence) {
      return `confidence-${confidence}`;
    },

    handleScroll() {
      if (!this.isLoading && this.hasMore) {
        const scrollPosition = window.innerHeight + window.scrollY;
        const bottomOfPage = document.documentElement.offsetHeight - 200;
        
        if (scrollPosition >= bottomOfPage) {
          this.loadMoreProducts();
        }
      }
    },

    loadMoreProducts() {
      if (this.isLoading || !this.hasMore) return;

      this.isLoading = true;
      const startIndex = this.displayedProducts.length;
      const endIndex = startIndex + this.pageSize;
      
      setTimeout(() => {
        const nextProducts = this.filteredProducts.slice(startIndex, endIndex);
        this.displayedProducts.push(...nextProducts);
        
        this.hasMore = this.displayedProducts.length < this.filteredProducts.length;
        this.isLoading = false;
      }, 500);
    },

    checkAuthentication() {
      const isAuth = localStorage.getItem('authenticated') === 'true';
      if (isAuth && !this.isAuthenticated) {
        this.isAuthenticated = true;
        this.currentView = 'attributeManagement';        
      } else if (!isAuth && this.isAuthenticated) {
        this.isAuthenticated = false;
        this.currentView = 'login';
      }
    },

    initializeApp() {
      this.checkAuthentication();
    }
  },
  async mounted() {
    this.isAuthenticated = localStorage.getItem('authenticated') === 'true';
    if (this.isAuthenticated) {      
      // Then load review status for each product, or just do it on demand
      for (const pId of this.allProductIds) {
        this.loadReviewStatus(pId);
      }
    }
  },
};
</script>

<style scoped>
.product-page {
  font-family: 'Inter', sans-serif;
  color: #2c3e50; /* Dark Blue */
  background-color: #ffffff; /* White */
}

.main-header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background-color: #2c3e50; /* Hardcoded Dark Blue */
  color: #fff;
  padding: 20px;
  text-align: center;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.main-header h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
}

.save-button {
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: #2c3e50; /* Dark Blue */
  color: #fff;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 16px;
  border-radius: 5px;
}

.save-button:hover {
  background-color: #34495e;
}

.main-content {
  margin-top: 90px; /* Adjust based on header height */
  display: flex;
}

.images-and-controls {
  display: flex;
}

.images-container {
  width: 500px; /* Adjusted width between 400px and 600px */
  padding: 20px;
  box-sizing: border-box;
  background-color: #fff;
  border-right: 1px solid #bdc3c7;
}

.page-navigation {
  margin-top: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.navigation-text {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.language-selector,
.flagging-strategy-selector,
.data-toggle {
  margin-top: 20px;
  text-align: center;
}

.content-container {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  max-height: calc(100vh - 90px); /* Full viewport height minus header */
}

.comparison-container {
  display: flex;
  flex-direction: row;
  gap: 30px;
  margin-bottom: 50px;
}

.comparison-section {
  flex: 1; /* Make sure both sections take equal space */
  background-color: #fff;
  padding: 30px;
  border-radius: 15px;
  box-sizing: border-box;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 30px;
  color: #2c3e50; /* Hardcoded Dark Blue */
  text-align: center;
}

.panel {
  margin-bottom: 30px;
  overflow-x: auto; /* Add horizontal scroll if content overflows */
}

.footer {
  padding: 20px;
  text-align: center;
  background-color: #2c3e50; /* Hardcoded Dark Blue */
  color: #fff;
}

.loading-state {
  text-align: center;
  margin-top: 100px;
  font-size: 20px;
  color: #2c3e50; /* Hardcoded Dark Blue */
}

.nav-button {
  background-color: #2c3e50; /* Hardcoded Dark Blue */
  border: none;
  padding: 8px;
  margin: 0 10px;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.3s, box-shadow 0.3s;
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.nav-button svg {
  width: 24px;
  height: 24px;
  stroke: #fff; /* Icon color set to white */
}

.nav-button:hover:not(:disabled) {
  background-color: #34495e; /* Slightly lighter shade on hover */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2); /* Add box-shadow on hover */
}

.nav-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.nav-button:disabled svg {
  stroke: #666; /* Icon color when disabled */
}

.article-number-text {
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50; /* Hardcoded Dark Blue */
}

.variant-number-text {
  display: block;
  font-size: 16px;
  font-weight: bold;
  color: #2c3e50; /* Hardcoded Dark Blue */
  margin-top: 5px;
  text-align: center;
}

.web-link {
  margin-top: 5px;
  text-align: center;
}

.web-link a {
  color: #e67e22; /* Hardcoded Accent Color */
  text-decoration: underline;
  font-size: 16px;
}

.brand-filter,
.variant-search,
.flagging-strategy-selector,
.reweighted-selector {
  margin-top: 20px;
  text-align: center;
}

.brand-filter label,
.variant-search label,
.flagging-strategy-selector label,
.reweighted-selector label {
  margin-right: 10px;
  font-weight: bold;
}

.variant-search input {
  padding: 5px;
  width: 200px;
}

.variant-search button {
  padding: 5px 10px;
  margin-left: 5px;
  background-color: #2c3e50;
  color: #fff;
  border: none;
  cursor: pointer;
}

.variant-search button:hover {
  background-color: #34495e;
}

/* Styles for the toggle switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
  margin-left: 10px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-switch .slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 20px;
}

.toggle-switch .slider:before {
  position: absolute;
  content: '';
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

.toggle-switch input:checked + .slider {
  background-color: #2196f3;
}

.toggle-switch input:checked + .slider:before {
  transform: translateX(20px);
}

.toggle-label {
  margin-left: 8px;
  font-size: 14px;
  vertical-align: middle;
}

/* Styles for the attribute filters */
.attribute-filter {
  margin-top: 20px;
  text-align: center;
}

.attribute-filter label {
  margin-right: 10px;
  font-weight: bold;
}

/* Media Queries */
@media (max-width: 1024px) {
  .main-content {
    flex-direction: column;
  }

  .images-and-controls {
    flex-direction: column;
  }

  .images-container {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #bdc3c7;
  }

  .comparison-container {
    flex-direction: column;
  }

  .content-container {
    max-height: none;
  }

  .page-navigation {
    flex-direction: column;
  }

  .back-button {
    position: absolute;
    top: 100px; /* Adjust as needed */
    left: 20px;
    background-color: transparent;
    color: #2c3e50;
    border: none;
    font-size: 18px;
    cursor: pointer;
  }

  .back-button:hover {
    text-decoration: underline;
  }

  .flagged-attribute-filter {
    margin-top: 20px;
    text-align: center;
  }

  .flagged-attribute-filter label {
    margin-right: 10px;
    font-weight: bold;
  }
}

/* Review Mode and Manor Attributes Grid Styles (outside media query) */
.review-mode-container {
  margin: 20px 0;
  text-align: center;
}

.review-mode-button {
  background-color: #ccc;
  color: #333;
  border: none;
  padding: 10px 20px;
  font-size: 16px;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.review-mode-button:hover {
  background-color: #bbb;
}

.review-mode-button.active {
  background-color: #2196f3;
  color: #fff;
}

.manor-attributes-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 20px;
}

.manor-attribute-row {
  display: flex;
  flex-direction: column;
}

.manor-attribute-label {
  font-weight: 600;
  margin-bottom: 5px;
}

.manor-attribute-select {
  padding: 8px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.manor-attribute-select:focus {
  border-color: #2196f3;
  outline: none;
  box-shadow: 0 0 0 2px rgba(33,150,243,0.2);
}
.confidence-indicator {
  width: 12px;
  height: 12px;
  display: inline-block;
  margin-left: 8px;
  border-radius: 50%;
  cursor: pointer;
  vertical-align: middle;
}
.confidence-certain {
  background-color: #28a745; /* green */
}
.confidence-pretty_sure {
  background-color: #ffc107; /* yellow */
}
.confidence-not_sure {
  background-color: #dc3545; /* red */
}
.attribute-row-content {
  display: flex;
  align-items: center;
}
.snackbar {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #333;
  color: #fff;
  padding: 10px 20px;
  border-radius: 5px;
  z-index: 9999;
  font-size: 14px;
  opacity: 0.9;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
}
.snackbar.success {
  background: #28a745; /* green */
}
.snackbar.error {
  background: #dc3545; /* red */
}

.review-mode-toggles {
  display: inline-flex;
  gap: 20px;
  margin-left: 20px;
  align-items: center;
}

.review-mode-toggles .toggle-label {
  color: #333;
  font-size: 14px;
  vertical-align: middle;
  cursor: pointer;
}
.review-mode-toggles .toggle-label input[type="checkbox"] {
  margin-right: 5px;
}

/* Improved styling for Manor Attributes in review mode */
.manor-attributes-grid-improved {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 25px;
  margin-top: 20px;
  background: #f7f9fc;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.manor-attribute-row-improved {
  display: flex;
  flex-direction: column;
}

.manor-attribute-label-improved {
  font-weight: 600;
  margin-bottom: 5px;
  font-size: 14px;
  color: #2c3e50;
}

.attribute-row-content-improved {
  display: flex;
  align-items: center;
  gap: 10px;
}

.manor-attribute-select-improved {
  padding: 8px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
  background: #fff;
}

.manor-attribute-select-improved:focus {
  border-color: #2196f3;
  outline: none;
  box-shadow: 0 0 0 2px rgba(33,150,243,0.2);
}

.confidence-indicator {
  width: 14px;
  height: 14px;
  display: inline-block;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.2s ease;
}
.confidence-indicator:hover {
  transform: scale(1.2);
}

.confidence-certain {
  background-color: #28a745; /* green */
}
.confidence-pretty_sure {
  background-color: #ffc107; /* yellow/orange */
}
.confidence-not_sure {
  background-color: #dc3545; /* red */
}

.manor-title {
  font-size: 18px;
  font-weight: bold;
  margin-top: 20px;
  margin-bottom: 15px;
  color: #2c3e50;
  border-bottom: 1px solid #ccc;
  padding-bottom: 5px;
}

/* Snackbar styling */
.snackbar {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #333;
  color: #fff;
  padding: 10px 20px;
  border-radius: 5px;
  z-index: 9999;
  font-size: 14px;
  opacity: 0.9;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
}
.snackbar.success {
  background: #28a745; /* green */
}
.snackbar.error {
  background: #dc3545; /* red */
}
.snackbar.warning {
  background: #ffc107; /* yellow/orange */
}
.reviewed-banner {
  background-color: #28a745 !important; /* Green if reviewed */
}
.not-reviewed-banner {
  background-color: #2c3e50 !important; /* Original color if not reviewed */
}
.right-panel {
  position: fixed;
  top: 100px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  z-index: 1000;
}

.toggle-button {
  background-color: #ccc;
  color: #333;
  border: none;
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  text-align: center;
  font-weight: 500;
}

.toggle-button:hover {
  background-color: #bbb;
}

.toggle-button.active {
  background-color: #2196f3;
  color: #fff;
}

.toggles-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.modal-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background-color: rgba(0,0,0,0.4);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-content {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  width: 400px;
}
.modal-buttons {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* A simple fade transition for v-if/v-else banners */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Our alert banner styling */
.alert-banner {
  position: fixed;
  top: 80px; /* Adjust to appear under your header if needed */
  left: 50%;
  transform: translateX(-50%);
  background-color: #ffb200; /* a golden/yellow color */
  color: #fff;
  padding: 12px 20px;
  border-radius: 4px;
  font-size: 16px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  z-index: 9999;
}
</style>

